"use client";
/**
 * Flow rail — the cross-page continuation of the /flow command center. A compact
 * pill in the top bar that shows the next step in your daily process and, on one
 * click, marks the step you're on done and takes you to the next page. This is
 * what makes the flow "seamless from one page to the next": you never have to
 * return to /flow to advance.
 *
 * Appears only once a flow is in progress today (you've entered a step from
 * /flow), and hides on /flow itself, on the last step, and on auth pages.
 */
import { useEffect, useMemo, useState } from "react";
import Link from "@/lib/spa/Link";
import { usePathname, useRouter } from "@/lib/spa/navigation";
import { FLOW_STEPS, TONE, stepById, phaseOf } from "@/lib/flow/steps";
import {
  loadProgress, subscribeProgress, markDone, setCurrent, nextStep, type FlowProgress,
} from "@/lib/flow/progress";

export function FlowRail() {
  const pathname = usePathname();
  const router = useRouter();
  const [progress, setProgress] = useState<FlowProgress | null>(null);

  useEffect(() => {
    const sync = () => setProgress(loadProgress());
    sync();
    return subscribeProgress(sync);
  }, []);

  const doneCount = progress?.completed.length ?? 0;
  const current = stepById(progress?.current ?? null);
  // Point forward from where you are; fall back to the first unfinished step.
  const nextId = useMemo(() => (progress ? nextStep(progress, current?.id ?? null) : null), [progress, current?.id]);
  const next = stepById(nextId);

  // Only show once a flow is underway, and not on the command center itself.
  const active = !!progress && (!!progress.current || doneCount > 0);
  if (!active || !progress) return null;
  if (pathname === "/flow" || pathname?.startsWith("/login") || pathname?.startsWith("/features")) return null;

  const advance = () => {
    if (current) markDone(current.id);
    if (next) {
      setProgress(setCurrent(next.id));
      router.push(next.href);
    } else {
      setProgress(loadProgress());
    }
  };

  const total = FLOW_STEPS.length;
  const tone = next ? TONE[phaseOf(next).tone] : TONE.bull;

  return (
    <div className="flex items-center overflow-hidden rounded-md border border-line bg-surface text-xs shadow-sm">
      <Link href="/flow" prefetch title="Back to Daily Flow" className="flex items-center gap-1.5 px-2 py-1 text-ink-muted transition hover:bg-surface-sunken hover:text-ink">
        <span className={`inline-block h-1.5 w-1.5 rounded-full ${tone.dot}`} />
        <span className="tnum font-semibold">{doneCount}/{total}</span>
      </Link>
      {next ? (
        <button
          onClick={advance}
          title={`Mark "${current?.title ?? "this step"}" done and go to ${next.title}`}
          className="flex items-center gap-1.5 border-l border-line px-2 py-1 text-ink transition hover:bg-accent-soft hover:text-accent-ink"
        >
          <span className="text-ink-faint">Next</span>
          <span className="max-w-[9rem] truncate font-semibold">{next.title}</span>
          <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
        </button>
      ) : (
        <span className="flex items-center gap-1 border-l border-line px-2 py-1 font-semibold text-bull">
          <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
          Complete
        </span>
      )}
    </div>
  );
}
